import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import PanelCard from './PanelCard';
import ExemptionDrawer from '../../../components/performance/ExemptionDrawer';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ReminderDrawer from '../../performance-reminders/ReminderDrawer';
import { formatDate } from '../../../utils/Utils';
import {
  Badge, Clickable, ProgressFill, ProgressTrack, Sub, Table, TabButton, Tabs,
} from '../styles';
import {
  fetchBreakdown, fetchOverdueTasks, formatNumber, hasValue, lowestUnits, metaOfBreakdown, metricOf, topWithOther, usePanel,
} from '../config';
import { TAB_TASKS, isRealKey, processHealthHref } from '../../../components/performance/processHealthLinks';

// Spec §6.4 — "Geciken Görevler" (ilk 5, gecikme günü azalan; satır menüsü Hatırlat + Muaf Tut), "Geciken Görevlerin Dağılımı"
// (Organizasyon / Rol / Aşama; ilk 7 + Diğer) ve "Tamamlanma Oranı (Birim Bazında)" (M02 artan, ilk 6).
export const taskLabel = (t, key) => t(`performance.operations.taskKey.${key}`, { defaultValue: key });
const stageLabel = (t, key) => t(`performance.insight.stage.${key}`, { defaultValue: key });

function assigneeText(t, row) {
  if (row.assigneeKind === 'hr_pool') return t('performance.operations.assignee.hrPool');
  if (row.assigneeKind === 'external') return t('performance.operations.assignee.external');
  return row.assigneeName || t('performance.operations.assignee.hidden');
}

export function OverdueTasksPanel({ filters, refreshKey, onChanged }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchOverdueTasks(filters), [key, refreshKey], !!filters.periodId);
  const [reminder, setReminder] = useState(null);
  const [exempt, setExempt] = useState(null);
  const rows = panel.data?.rows || [];
  const all = processHealthHref(filters, { tab: TAB_TASKS, task: { overdue: 'true' } });
  return (
    <PanelCard
      panelKey="overdueTasks" title={t('performance.operations.panel.overdueTasks')} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.operations.panel.noOverdue')}
      actions={all && panel.data ? <Link to={all} data-link="all">{t('performance.operations.panel.seeAll', { count: panel.data.total })}</Link> : null}
    >
      <Table>
        <colgroup><col style={{ width: '34%' }} /><col style={{ width: '18%' }} /><col style={{ width: '26%' }} /><col style={{ width: '16%' }} /><col style={{ width: '6%' }} /></colgroup>
        <thead>
          <tr>
            <th>{t('performance.operations.col.task')}</th>
            <th>{t('performance.operations.col.due')}</th>
            <th>{t('performance.operations.col.assignee')}</th>
            <th>{t('performance.operations.col.overdueDays')}</th>
            <th aria-label={t('performance.operations.col.actions')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.taskId} data-row={r.taskId}>
              <td>
                <div>{taskLabel(t, r.taskKey)}</div>
                <Sub>{`${r.employeeName || '-'} · ${stageLabel(t, r.stageKey)}`}</Sub>
              </td>
              <td>
                <div>{r.dueAt ? formatDate(r.dueAt, lang) : '-'}</div>
                <Sub>{t(`performance.operations.taskStatus.${r.status}`, { defaultValue: r.status })}</Sub>
              </td>
              <td>
                <div>{assigneeText(t, r)}</div>
                {r.assigneeTitle ? <Sub>{r.assigneeTitle}</Sub> : null}
              </td>
              <td><Badge $tone="danger">{t('performance.operations.days', { count: r.overdueDays || 0 })}</Badge></td>
              <td>
                <OptionsMenu
                  row={r}
                  actions={[
                    // Seçim açılışta bir kez kurulur: her çizimde yeni nesne ReminderDrawer önizlemesini yeniden gönderirdi (Önemli 3).
                    { type: 'send', label: t('performance.operations.rowAction.remind'), onClick: () => setReminder({ periodId: filters.periodId, taskIds: [r.taskId] }) },
                    { type: 'waive', label: t('performance.operations.rowAction.exempt'), onClick: () => setExempt(r) },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ReminderDrawer
        open={!!reminder}
        selection={reminder}
        onClose={() => setReminder(null)}
        onSent={() => { setReminder(null); onChanged?.(); }}
      />
      <ExemptionDrawer
        open={!!exempt}
        task={exempt}
        onClose={() => setExempt(null)}
        onDone={() => { setExempt(null); onChanged?.(); }}
      />
    </PanelCard>
  );
}

export const DISTRIBUTION_TABS = ['department', 'assigneeRole', 'stage'];

export function OverdueDistributionPanel({ filters, onMeta }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [tab, setTab] = useState('department');
  const [table, setTable] = useState(false);
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, ['M04'], tab), [key, tab], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('overdueDistribution', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);

  const label = (c) => {
    if (tab === 'stage') return stageLabel(t, c.key);
    if (tab === 'assigneeRole') return t(`performance.reminder.role.${c.key}`, { defaultValue: c.key || t('performance.operations.unassigned') });
    return c.label || t('performance.operations.unassigned');
  };
  const items = (panel.data?.cells || []).map((c) => ({ key: c.key, name: label(c), value: Number(metricOf(c.metrics, 'M04')?.value || 0) }));
  const { items: top, other } = topWithOther(items);
  const data = other ? [...top, { key: null, name: t('performance.operations.other'), value: other, other: true }] : top;
  // Çubuk → Görevler, ilgili süzgeç + gecikmiş; departman ortak organizasyon süzgecinin yerine geçer (spec §19/4).
  const linkFor = (d) => {
    if (d.other || !isRealKey(d.key)) return null;
    if (tab === 'department') return processHealthHref(filters, { tab: TAB_TASKS, departmentIds: [d.key], task: { overdue: 'true' } });
    if (tab === 'assigneeRole') return processHealthHref(filters, { tab: TAB_TASKS, task: { roles: [d.key], overdue: 'true' } });
    return processHealthHref(filters, { tab: TAB_TASKS, task: { stage: d.key, overdue: 'true' } });
  };

  return (
    <PanelCard
      panelKey="overdueDistribution" title={t('performance.operations.panel.overdueDistribution')} span={3}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={data.length === 0}
      emptyText={t('performance.operations.panel.noOverdue')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <Tabs role="tablist">
        {DISTRIBUTION_TABS.map((k) => (
          <TabButton key={k} role="tab" aria-selected={tab === k} $active={tab === k} onClick={() => setTab(k)} data-tab={k}>
            {t(`performance.operations.distributionTab.${k}`)}
          </TabButton>
        ))}
      </Tabs>
      {table ? (
        <Table>
          <thead><tr><th>{t(`performance.operations.distributionTab.${tab}`)}</th><th>{t('performance.operations.series.overdue')}</th></tr></thead>
          <tbody>
            {data.map((d) => {
              // Tablo görünümü erişilebilir alternatiftir: ayrıntıya iniş klavyeyle de yapılabilsin.
              const href = linkFor(d);
              return (
                <tr key={d.key || d.name} data-row={d.key || 'other'}>
                  <td>{href ? <Clickable onClick={() => history.push(href)} data-cell={`${d.key}:overdue`}>{d.name}</Clickable> : d.name}</td>
                  <td>{d.value}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(120, data.length * 30) }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Bar
                dataKey="value" name={t('performance.operations.series.overdue')} fill={theme.colors.auxiliar.danger}
                onClick={(d) => { const href = linkFor(d.payload || d); if (href) history.push(href); }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}

export function UnitCompletionPanel({ filters, onMeta }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const key = JSON.stringify(filters);
  const panel = usePanel(() => fetchBreakdown(filters, ['M01', 'M02', 'M04'], 'department'), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('unitCompletion', metaOfBreakdown(panel.data)); }, [panel.data, onMeta]);
  const units = lowestUnits((panel.data?.cells || []).filter((c) => c.key));
  const detail = processHealthHref(filters);
  return (
    <PanelCard
      panelKey="unitCompletion" title={t('performance.operations.panel.unitCompletion')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={units.length === 0}
      actions={detail ? <Link to={detail} data-link="detail">{t('performance.operations.panel.detail')}</Link> : null}
    >
      <Table>
        <colgroup><col style={{ width: '34%' }} /><col style={{ width: '16%' }} /><col style={{ width: '34%' }} /><col style={{ width: '16%' }} /></colgroup>
        <thead>
          <tr>
            <th>{t('performance.operations.col.unit')}</th>
            <th>{t('performance.operations.col.coverage')}</th>
            <th>{t('performance.operations.col.completion')}</th>
            <th>{t('performance.operations.col.overdue')}</th>
          </tr>
        </thead>
        <tbody>
          {units.map(({ cell, m01, m02, m04 }) => {
            const href = isRealKey(cell.key) ? processHealthHref(filters, { departmentIds: [cell.key] }) : null;
            const name = cell.label || t('performance.operations.unassigned');
            return (
              <tr key={cell.key} data-row={cell.key}>
                <td title={name}>{href ? <Clickable onClick={() => history.push(href)}>{name}</Clickable> : name}</td>
                <td>{hasValue(m01) ? formatNumber(m01.value, i18n.language, 0) : '-'}</td>
                <td>
                  {hasValue(m02) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ProgressTrack><ProgressFill $pct={m02.value} /></ProgressTrack>
                      <span>{`${formatNumber(m02.value, i18n.language)}%`}</span>
                    </div>
                  ) : t('performance.operations.kpi.noData')}
                </td>
                <td>{hasValue(m04) ? formatNumber(m04.value, i18n.language, 0) : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </PanelCard>
  );
}
